import React, { useState, useMemo } from 'react';
import { X, Search, Filter, SortAsc, ExternalLink, Files, Calendar, ChevronDown } from 'lucide-react';
import { HomeworkAttachment, Lesson } from '../types';
import { useTranslation } from '../lib/translations';
import { getFileIcon } from '../lib/fileUtils';
import { format } from 'date-fns';
import { enUS, fr } from 'date-fns/locale';

interface SharedFilesModalProps {
  isOpen: boolean;
  onClose: () => void;
  lessons: Lesson[];
}

interface EnrichedFile extends HomeworkAttachment {
  sharedAt: Date;
}

const SharedFilesModal: React.FC<SharedFilesModalProps> = ({ isOpen, onClose, lessons }) => {
  const { t, language } = useTranslation();
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('date-newest');

  const locale = language === 'fr' ? fr : enUS;

  const allFiles = useMemo(() => {
    const files: EnrichedFile[] = [];
    // Lessons are usually sorted desc, but we iterate and map the date
    lessons.forEach(l => {
      if (l.homework?.attachments) {
        l.homework.attachments.forEach(a => {
          // Check if already added (unique files based on driveId)
          if (!files.find(f => f.driveId === a.driveId)) {
            files.push({
              ...a,
              sharedAt: l.start.toDate()
            });
          }
        });
      }
    });
    return files;
  }, [lessons]);

  const filteredFiles = useMemo(() => {
    let result = allFiles.filter(f => 
      f.name.toLowerCase().includes(search.toLowerCase())
    );

    if (filterType !== 'all') {
      if (filterType === 'image') result = result.filter(f => f.mimeType.startsWith('image/'));
      else if (filterType === 'audio') result = result.filter(f => f.mimeType.startsWith('audio/'));
      else if (filterType === 'video') result = result.filter(f => f.mimeType.startsWith('video/'));
      else if (filterType === 'pdf') result = result.filter(f => f.mimeType === 'application/pdf');
      else if (filterType === 'doc') result = result.filter(f => f.mimeType.includes('document') || f.mimeType.includes('word'));
    }

    result.sort((a, b) => {
      if (sortBy === 'name-az') return a.name.localeCompare(b.name);
      if (sortBy === 'name-za') return b.name.localeCompare(a.name);
      if (sortBy === 'date-newest') return b.sharedAt.getTime() - a.sharedAt.getTime();
      if (sortBy === 'date-oldest') return a.sharedAt.getTime() - b.sharedAt.getTime();
      return 0;
    });

    return result;
  }, [allFiles, search, filterType, sortBy]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-slate-950/70 backdrop-blur-md animate-in fade-in duration-300 !mt-0">
      <div className="bg-white dark:bg-slate-900 w-full max-w-4xl max-h-[85vh] rounded-2xl border border-slate-200 dark:border-white/5 shadow-2xl flex flex-col overflow-hidden animate-in zoom-in duration-300">
        
        {/* Header */}
        <div className="p-6 sm:p-8 border-b border-slate-100 dark:border-white/5 flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/20 text-purple-600 rounded-xl flex items-center justify-center">
              <Files size={24} />
            </div>
            <div>
              <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">{t.studentDetail.filesTitle}</h2>
              <p className="text-slate-500 text-xs font-medium">{t.studentDetail.filesDesc}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-white transition-all active:scale-90">
            <X size={24} />
          </button>
        </div>

        {/* Search & Controls */}
        <div className="p-6 bg-slate-50 dark:bg-slate-950/50 border-b border-slate-100 dark:border-white/5 space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input 
                type="text" 
                placeholder={t.studentDetail.searchFiles} 
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-xl py-3 pl-12 pr-4 text-sm font-medium focus:ring-4 focus:ring-purple-500/10 focus:border-purple-500 outline-none transition-all shadow-sm"
              />
            </div>
            
            <div className="flex gap-3">
              <div className="relative flex items-center bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-xl px-3 shadow-sm">
                <Filter size={14} className="text-slate-400 mr-2" />
                <select 
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="bg-transparent text-xs font-black uppercase tracking-widest text-slate-600 dark:text-slate-300 outline-none cursor-pointer py-2.5 pr-6 appearance-none"
                >
                  <option value="all">{t.studentDetail.allTypes}</option>
                  <option value="image">Images</option>
                  <option value="audio">Audio</option>
                  <option value="video">Video</option>
                  <option value="pdf">PDF</option>
                  <option value="doc">Documents</option>
                </select>
                <ChevronDown size={12} className="absolute right-3 pointer-events-none text-slate-400" />
              </div>

              <div className="relative flex items-center bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-xl px-3 shadow-sm">
                <SortAsc size={14} className="text-slate-400 mr-2" />
                <select 
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="bg-transparent text-xs font-black uppercase tracking-widest text-slate-600 dark:text-slate-300 outline-none cursor-pointer py-2.5 pr-6 appearance-none"
                >
                  <option value="date-newest">{t.common.newest}</option>
                  <option value="date-oldest">{t.common.oldest}</option>
                  <option value="name-az">{t.students.sorting.nameAZ}</option>
                  <option value="name-za">{t.students.sorting.nameZA}</option>
                </select>
                <ChevronDown size={12} className="absolute right-3 pointer-events-none text-slate-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 sm:p-8 custom-scrollbar">
          {filteredFiles.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center opacity-50 py-20">
              <Files size={48} className="text-slate-300 dark:text-slate-700 mb-4" />
              <p className="text-sm font-bold text-slate-400 uppercase tracking-widest italic">{t.studentDetail.noFilesFound}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {filteredFiles.map((file) => (
                <a 
                  key={file.driveId}
                  href={file.webViewLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-4 bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-white/5 rounded-xl hover:border-purple-500/40 hover:bg-purple-50 dark:hover:bg-purple-900/10 transition-all group shadow-sm"
                >
                  <div className="w-12 h-12 bg-slate-50 dark:bg-slate-900 rounded-lg flex items-center justify-center shrink-0 border border-slate-100 dark:border-white/5 group-hover:scale-110 transition-transform">
                    {getFileIcon(file.mimeType, 24)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-bold text-slate-900 dark:text-white truncate group-hover:text-purple-600 transition-colors">{file.name}</h4>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="flex items-center gap-1 text-[9px] font-black uppercase text-slate-400 tracking-tighter">
                        <Calendar size={10} /> {format(file.sharedAt, 'MMM d, yyyy', { locale })}
                      </span>
                      <span className="w-1 h-1 rounded-full bg-slate-200 dark:bg-slate-800" />
                      <span className="text-[9px] font-black uppercase text-slate-400 tracking-tighter truncate max-w-[100px]">
                        {file.mimeType.split('/').pop()?.toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="p-2 text-slate-300 group-hover:text-purple-600">
                    <ExternalLink size={16} />
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SharedFilesModal;